import React, { Component, Fragment } from 'react';
import styleable from 'react-styleable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { DataTable, DatePicker } from '@3plearning/react-components';
import cloneDeep from 'lodash/cloneDeep';
import css from './single-class.scss';
import classActions, {
    ACTIVITY_MASTERY_CLASS_RETRIEVED
} from './single-class-actions';
import {
    SINGLE_CLASS_TABLE_CONFIG,
    GROUP_NAME,
    COURSE_NAME,
    TOTAL_ACTIVITIES,
    STUDENT_NAME
} from './single-class-constants';
import ActivityStatusBar from '../../../components/activity-status-bar/activity-status-bar';
import TopicActivityStrengthWeakness from '../common/topic-activity-strength-weakness/topic-activity-strength-weakness';
import activeFiltersActions from '../../../components/report-filters/redux/active-filters/active-filters-actions';
import {
    SELECTED_GROUP,
    SELECTED_STUDENT,
    SELECTED_DATE,
    SELECTED_CLASS
} from '../../../components/report-filters/filter-constants';
import { activeFiltersPropType } from '../../../components/report-filters/filter-prop-types';
import singleClassPropTypes from './single-class-prop-types';
import activeFiltersSelectors from '../../../components/report-filters/redux/active-filters/active-filters-selectors';
import FilterHelper from '../../../helpers/filter-helper';

import ActivityMasteryLegends from '../common/activity-mastery-legends/activity-mastery-legends';
import { translateTableLabels } from '../../../helpers/locale-helpers';

/**
 * SingleClass component
 * Displays Activity Mastery table and strengths & weaknesses tabs for single classes
 */
export class SingleClass extends Component {
    static propTypes = {
        activityMasteryData: singleClassPropTypes.activityMasteryData,
        activeFilters: activeFiltersPropType,
        getActivityMasteryData: PropTypes.func,
        updateActiveFilter: PropTypes.func,
        invalidRequest: PropTypes.string
    };

    constructor(props) {
        super(props);
        this.state = {
            tableConfig: null,
            strengthData: { topics: [], activties: [] },
            weaknessData: { topics: [], activities: [] },
            filteredGroups: []
        };
        this.i18n = window.i18n;
    }

    componentDidMount() {
        const { activeFilters } = this.props;
        if (FilterHelper.shouldLoadSingleClass(activeFilters)) {
            this.getClassActivityMasteryData(
                activeFilters[SELECTED_DATE],
                activeFilters[SELECTED_CLASS].id
            );
        }
    }

    componentWillReceiveProps(newProps) {
        const {
            activeFilters,
            activityMasteryData,
            invalidRequest,
            updateActiveFilter
        } = this.props;
        const {
            activeFilters: newActiveFilters,
            activityMasteryData: newActivityMasteryData,
            invalidRequest: newInvalidRequest
        } = newProps;

        if (
            FilterHelper.shouldReloadSingleClass(
                activeFilters,
                newActiveFilters
            )
        ) {
            this.getClassActivityMasteryData(
                newActiveFilters[SELECTED_DATE],
                newActiveFilters[SELECTED_CLASS].id
            );
        }

        if (activityMasteryData !== newActivityMasteryData) {
            this.setActivityMasteryData(
                newActivityMasteryData,
                activeFilters[SELECTED_GROUP].id
            );
        }

        if (
            invalidRequest !== newInvalidRequest &&
            newInvalidRequest === ACTIVITY_MASTERY_CLASS_RETRIEVED
        ) {
            if (
                newActiveFilters[SELECTED_DATE].type !==
                DatePicker.THIS_SCHOOL_YEAR
            ) {
                updateActiveFilter(SELECTED_DATE, {
                    type: DatePicker.THIS_SCHOOL_YEAR
                });
            }
        }
    }

    getClassActivityMasteryData(selectedDate, classId) {
        this.props.getActivityMasteryData(classId, selectedDate);
    }

    setActivityMasteryData(activityMasteryData, groupId) {
        groupId = +groupId || 0;
        let strengthData;
        let weaknessData;
        let filteredGroups =
            activityMasteryData.activityMastery.groupActivityMastery;
        if (groupId) {
            filteredGroups = filteredGroups.filter(
                (group) => group.id === groupId
            );
            strengthData = filteredGroups[0].strengths;
            weaknessData = filteredGroups[0].weaknesses;
        } else {
            strengthData = activityMasteryData.strengths;
            weaknessData = activityMasteryData.weaknesses;
        }
        this.setState({
            strengthData,
            weaknessData,
            filteredGroups
        });
        this.setTableConfig(activityMasteryData, groupId);
    }

    /**
     * Update table config, setting properties which require the backend data
     */
    setTableConfig = (payload, groupId) => {
        let tableConfig = cloneDeep(SINGLE_CLASS_TABLE_CONFIG);
        const groupCol = this.getColFromConfig(tableConfig, GROUP_NAME);
        groupCol.name = payload.teacherName;
        if (!groupId) {
            groupCol.sort = { sortAs: 'string', sortDir: 'desc' };
            groupCol.onClick = (selectedGroup) =>
                this.loadNewPage(SELECTED_GROUP, selectedGroup);
        }

        let activityMasteryData = payload.activityMastery;
        if (groupId) {
            activityMasteryData = payload.activityMastery.groupActivityMastery.find(
                (group) => group.id === groupId
            );
        }
        this.getColFromConfig(
            tableConfig,
            COURSE_NAME
        ).headerTransform = () => (
            <ActivityStatusBar activityMastery={activityMasteryData} />
        );

        this.getColFromConfig(
            tableConfig,
            TOTAL_ACTIVITIES
        ).name = `${payload.activityMastery.totalActivities}`;

        this.getColFromConfig(tableConfig.nestedData, STUDENT_NAME).onClick = (
            selectedStudent
        ) => this.loadNewPage(SELECTED_STUDENT, selectedStudent);

        tableConfig = translateTableLabels(tableConfig);

        this.setState({ tableConfig });
    };

    getColFromConfig = (config, colId) =>
        config.cols.find((col) => col.id === colId);

    /**
     * Loads the group or student page when user clicks on item in table
     */
    loadNewPage = (filterName, selectedItem) => {
        this.props.updateActiveFilter(filterName, {
            id: selectedItem.id,
            name: selectedItem.name
        });
    };

    render() {
        const {
            tableConfig,
            filteredGroups,
            strengthData,
            weaknessData
        } = this.state;

        return (
            <Fragment>
                <ActivityMasteryLegends />
                <div className={css.component}>
                    <div className={css['data-table']}>
                        <DataTable config={tableConfig} data={filteredGroups} />
                    </div>
                    <div className={css['strength-weakness']}>
                        <TopicActivityStrengthWeakness
                            strengths={strengthData}
                            weaknesses={weaknessData}
                        />
                    </div>
                </div>
            </Fragment>
        );
    }
}

function mapStateToProps(state) {
    return {
        activeFilters: activeFiltersSelectors.selectActiveFilters(state),
        activityMasteryData: state.activityMasterySingleClass,
        invalidRequest: state.invalidRequest
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            getActivityMasteryData: classActions.getActivityMasteryData,
            updateActiveFilter: activeFiltersActions.updateActiveFilter
        },
        dispatch
    );
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(styleable(css)(SingleClass));



// WEBPACK FOOTER //
// ./containers/activity-mastery/single-class/single-class.js